/**
 * Word-set matcher over folded word tokens (ENGINE-SPEC §5 Matching: TELLS, HEDGES).
 *
 * A `Map` replaces the seed's plain-object lookup. The seed's `TELLS[w]` also hits inherited
 * keys (`constructor`, `toString`, `valueOf`, ...), which poisons the LX-01 sum; the compiled
 * matcher never does (candidate change C-LEX-PROTO).
 */

import { LOWER } from './chars.ts';

export type WordEmit = (start: number, end: number, word: string, weight: number) => void;

export class WordSet {
	readonly size: number;
	private readonly weights: Map<string, number>;

	constructor(entries: readonly (readonly [string, number])[] | readonly string[]) {
		this.weights = new Map();
		for (const e of entries) {
			if (typeof e === 'string') this.weights.set(e, 1);
			else this.weights.set(e[0], e[1]);
		}
		this.size = this.weights.size;
	}

	has(word: string): boolean {
		return this.weights.has(word);
	}

	/** Weight of a folded word, 0 when absent. */
	weight(word: string): number {
		return this.weights.get(word) ?? 0;
	}

	words(): IterableIterator<string> {
		return this.weights.keys();
	}

	/** id-keyed view for an intern table: `resolve(word)` returns the token id or undefined. */
	idWeights(resolve: (word: string) => number | undefined): Map<number, number> {
		const out = new Map<number, number>();
		for (const [w, x] of this.weights) {
			const id = resolve(w);
			if (id !== undefined) out.set(id, x);
		}
		return out;
	}

	/**
	 * Scan raw text with the seed's tokenizer (lower-case the text, then take runs of
	 * [a-z\u00c0-\u024f] followed by [a-z\u00c0-\u024f'\u2019-]), emitting each token that is in
	 * the set. Offsets are `offsetBase` plus the index in `text` (lower-casing is per code unit,
	 * so offsets are those of the original string). Returns the number of emitted tokens.
	 */
	scan(text: string, offsetBase: number, emit: WordEmit): number {
		let n = 0;
		scanWordTokens(text, (s, e) => {
			const w = foldSlice(text, s, e);
			const x = this.weights.get(w);
			if (x !== undefined) {
				n++;
				emit(offsetBase + s, offsetBase + e, w, x);
			}
		});
		return n;
	}
}

const isLetter = (c: number): boolean => (c >= 97 && c <= 122) || (c >= 0xc0 && c <= 0x24f);

/** Emit [start, end) of each seed word token of `text` (lower-cased per code unit). */
export function scanWordTokens(text: string, emit: (start: number, end: number) => void): void {
	const n = text.length;
	let i = 0;
	while (i < n) {
		const c0 = LOWER[text.charCodeAt(i)] as number;
		if (c0 === 0) {
			// U+0130 lower-cases to "i" + U+0307: a one-letter token, then a break.
			if (text.charCodeAt(i) === 0x130) emit(i, i + 1);
			i++;
			continue;
		}
		if (!isLetter(c0)) {
			i++;
			continue;
		}
		let j = i + 1;
		while (j < n) {
			const c = LOWER[text.charCodeAt(j)] as number;
			if (c === 0) {
				if (text.charCodeAt(j) === 0x130) j++;
				break;
			}
			if (isLetter(c) || c === 39 || c === 0x2019 || c === 45) j++;
			else break;
		}
		emit(i, j);
		i = j;
	}
}

/** The token text as the seed sees it (U+0130 only ever ends or forms a token: its "i" part). */
export function foldSlice(text: string, start: number, end: number): string {
	let out = '';
	for (let i = start; i < end; i++) {
		const c = LOWER[text.charCodeAt(i)] as number;
		out += c === 0 ? 'i̇' : String.fromCharCode(c);
	}
	return out;
}
